import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ShoppingCart, Heart } from 'lucide-react';
import { useStoreData, getMainImage } from '../hooks/useStoreData';
import { useCart } from '../contexts/CartContext';
import Header from '../components/Header';
import Cart from '../components/Cart';
import Footer from '../components/Footer';
import ProductDetail from '../components/ProductDetail';
import LoadingScreen from '../components/LoadingScreen';
import SEO from '../components/SEO';

const ProductCard = ({ product, saleSettings, onProductClick }) => {
  const [isLiked, setIsLiked] = useState(false);
  const mainImage = getMainImage(product);
  const isOutOfStock = product.stock === 0;
  const { addToCart } = useCart();

  // Calculate sale price if sale is enabled
  const globalSaleEnabled = saleSettings?.enabled && saleSettings?.percentage > 0;
  const productSaleEnabled = product.saleEnabled !== false;
  const salePercentage = product.salePercentage || saleSettings?.percentage || 0;

  const isSaleActive = globalSaleEnabled && productSaleEnabled && salePercentage > 0;
  const salePrice = isSaleActive
    ? Math.round(product.price - (product.price * salePercentage / 100))
    : product.price;

  const handleAddToCart = (e) => {
    e.stopPropagation();

    const productToAdd = {
      ...product,
      price: salePrice,
      originalPrice: product.price,
      saleDiscount: isSaleActive ? salePercentage : 0
    };

    addToCart(productToAdd, 1);
  };

  return (
    <div className="group cursor-pointer" onClick={() => onProductClick(product)}>
      <div className="relative overflow-hidden rounded-lg mb-4 bg-gray-100">
        <img
          src={mainImage}
          alt={product.name}
          className="w-full h-[400px] object-cover transition-transform duration-500 group-hover:scale-105"
        />
        {isOutOfStock && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
            <span className="bg-white text-gray-800 px-4 py-2 rounded-full font-semibold">Out of Stock</span>
          </div>
        )}
        <div className="absolute top-4 left-4 flex flex-col gap-2">
          {isSaleActive && !isOutOfStock && (
            <div className="bg-red-600 text-white px-3 py-1 rounded-full font-semibold text-sm">
              {saleSettings.label || 'SALE'} {salePercentage}% OFF
            </div>
          )}
          {product.newArrival && !isOutOfStock && (
            <div className="bg-green-600 text-white px-3 py-1 rounded-full font-semibold text-sm">
              NEW ARRIVAL
            </div>
          )}
        </div>
        <button
          onClick={(e) => {
            e.stopPropagation();
            setIsLiked(!isLiked);
          }}
          className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm p-2 rounded-full hover:bg-white transition-all duration-300 transform hover:scale-110"
        >
          <Heart
            size={20}
            className={`transition-colors ${
              isLiked ? 'fill-red-500 text-red-500' : 'text-gray-600'
            }`}
          />
        </button>
        {!isOutOfStock && (
          <button
            onClick={handleAddToCart}
            className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-[#4a1a1f] text-white px-6 py-2 rounded-full opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-y-4 group-hover:translate-y-0 hover:bg-[#5a2a2f] flex items-center gap-2"
          >
            <ShoppingCart size={18} />
            <span className="text-sm uppercase tracking-wider">Add to Cart</span>
          </button>
        )}
      </div>
      <div className="text-center">
        <h3 className="text-lg font-medium mb-2 group-hover:text-[#4a1a1f] transition-colors">
          {product.name}
        </h3>
        <div className="flex items-center justify-center gap-2">
          {isSaleActive ? (
            <>
              <p className="text-gray-400 line-through text-sm">PKR {product.price.toLocaleString()}</p>
              <p className="text-red-600 font-bold text-lg">PKR {salePrice.toLocaleString()}</p>
            </>
          ) : (
            <p className="text-[#4a1a1f] font-semibold text-lg">PKR {product.price.toLocaleString()}</p>
          )}
        </div>
        {product.stock > 0 && product.stock < 10 && (
          <p className="text-sm text-orange-600 mt-1">Only {product.stock} left in stock!</p>
        )}
      </div>
    </div>
  );
};

const CategoryPage = () => {
  const navigate = useNavigate();
  const { category: categorySlug } = useParams();
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const { products, categories, settings, loading } = useStoreData();
  const { getCartCount } = useCart();

  // Find category details
  const category = categories.find(cat => cat.slug === categorySlug);

  // Filter products based on category
  const filteredProducts = categorySlug === 'new-arrivals'
    ? products.filter(p => p.newArrival === true)
    : products.filter(p => p.category === categorySlug);

  const handleProductClick = (product) => {
    setSelectedProduct(product);
  };

  const handleCloseDetail = () => {
    setSelectedProduct(null);
  };

  // Show loading screen while data is being fetched or if critical data is missing
  const isDataReady = !loading && products.length >= 0 && categories.length >= 0 && settings;

  if (!isDataReady) {
    return <LoadingScreen />;
  }

  // Generate SEO data based on category
  const categoryName = categorySlug === 'new-arrivals' ? 'New Arrivals' : category?.name || 'Products';
  const seoTitle = `${categoryName} - Premium Abayas | Rizzahra`;
  const seoDescription = `Shop ${categoryName.toLowerCase()} at Rizzahra. ${filteredProducts.length} elegant and modern abayas available. Premium quality Islamic modest fashion.`;

  return (
    <div className="min-h-screen bg-gray-50 animate-fade-in">
      <SEO
        title={seoTitle}
        description={seoDescription}
        keywords={`${categoryName}, abayas, islamic clothing, modest fashion, ${categorySlug}`}
        url={`/category/${categorySlug}`}
      />
      <Header
        cartCount={getCartCount()}
        onCartClick={() => setIsCartOpen(true)}
        onAdminClick={() => navigate('/admin')}
      />

      {/* Page Header */}
      <section className="bg-[#4a1a1f] text-white py-12">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl font-bold uppercase tracking-wider">
            {categorySlug === 'new-arrivals' ? 'New Arrivals' : category?.name || 'Products'}
          </h1>
          <p className="text-gray-300 mt-2">
            {filteredProducts.length} {filteredProducts.length === 1 ? 'product' : 'products'} available
          </p>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-16 px-4">
        <div className="container mx-auto">
          {filteredProducts.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-gray-600 text-lg">No products found in this category.</p>
              <button
                onClick={() => navigate('/')}
                className="mt-6 bg-[#4a1a1f] text-white px-6 py-3 rounded-full hover:bg-[#5a2a2f] transition-colors"
              >
                Browse All Products
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
              {filteredProducts.map((product) => (
                <ProductCard
                  key={product.id}
                  product={product}
                  saleSettings={settings?.sale}
                  onProductClick={handleProductClick}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
      <Cart isOpen={isCartOpen} onClose={() => setIsCartOpen(false)} />

      {/* Product Detail Modal */}
      <ProductDetail
        product={selectedProduct}
        isOpen={!!selectedProduct}
        onClose={handleCloseDetail}
        saleSettings={settings?.sale}
      />
    </div>
  );
};

export default CategoryPage;
